import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';

import Button from 'components/StyledButton/StyledButton';
import Checkbox from '@material-ui/core/Checkbox';
import FormControlLabel from '@material-ui/core/FormControlLabel';

import ResetPasswordDialog from '../../dialogs/ResetPasswordDialog/ResetPasswordDialog';
import logo from '../../logo.png';
import { setFromApi } from '../../redux/user/userSlice';
import api from '../../utils/api';
import snackbar from '../../utils/snackbar';
import { ButtonContainer, CheckboxContainer, FormContainer, Logo, ProgressPlaceholder, StyledLink, StyledProgress, StyledTextField, Title } from './styled';

const LoginPage = () => {
    const dispatch = useDispatch();
    const { t } = useTranslation();

    const [credentials, setCredentials] = useState({
        username: '',
        password: '',
        rememberMe: false
    });

    const [isLoading, setIsLoading] = useState(false);
    const [isResetPasswordDialogOpen, setIsResetPasswordDialogOpen] = useState(false);

    const handleLogin = useCallback(() => {
        if (isLoading) return;

        setIsLoading(true);
        api.login(credentials.username, credentials.password, credentials.rememberMe).then(() => {
            // if login success, fetch the user data and save them in the Redux state
            api.request('/users/me').then(profile => {
                dispatch(setFromApi(profile));
            });
        }).catch(res => {
            snackbar.handleApiError(res);
        }).finally(() => {
            setIsLoading(false);
        });
    }, [credentials, dispatch, isLoading]);

    const handleInputChange = useCallback((field, e) => {
        const value = e.target.value;
        setCredentials((currentCredentials) => {
            return {...currentCredentials, [field]: value};
        });
    }, []);

    const handleRememberMeChange = useCallback((e) => {
        const checked = e.target.checked;
        setCredentials(currentCredentials => {
            return {...currentCredentials, rememberMe: checked};
        });
    }, []);

    const handleResetPasswordClick = useCallback((e: any) => {
        e.preventDefault();
        setIsResetPasswordDialogOpen(true);
    }, []);

    const handleKeyDown = useCallback((e) => {
        if (e.keyCode === 13) {
            handleLogin();
        }
    }, [handleLogin]);

    return (
        <>
            <Logo src={logo} alt='logo' />
            <FormContainer>
                <Title variant='h4'>
                    {t('pages.login.title')}
                </Title>

                <StyledTextField
                    label={t('pages.login.username')}
                    variant='outlined'
                    onChange={(e) => handleInputChange('username', e)}
                    onKeyDown={handleKeyDown}
                    fullWidth
                    autoFocus
                />

                <StyledTextField
                    label={t('pages.login.password')}
                    variant='outlined'
                    type='password'
                    onChange={(e) => handleInputChange('password', e)}
                    onKeyDown={handleKeyDown}
                    fullWidth
                />

                <StyledLink href='#' onClick={(e: any) => handleResetPasswordClick(e)}>Password dimenticata?</StyledLink>

                <CheckboxContainer>
                    <FormControlLabel
                        control={
                            <Checkbox
                                checked={credentials.rememberMe}
                                onChange={handleRememberMeChange}
                                name={t('pages.login.remember_me')}
                                color='primary'
                            />
                        }
                        label={t('pages.login.remember_me')}
                    />
                </CheckboxContainer>

                {isLoading ?
                    <StyledProgress /> :
                    <ProgressPlaceholder />
                }

                <ButtonContainer>
                    <Button variant='contained' onClick={handleLogin}>
                        {t('pages.login.login_button')}
                    </Button>
                </ButtonContainer>

                <ResetPasswordDialog open={isResetPasswordDialogOpen} onClose={() => setIsResetPasswordDialogOpen(false)} />
            </FormContainer>
        </>
    );
};

export default LoginPage;
